'use client';
import ProductCard from '@/components/productCard';
import CDNImage from '@/components/cdnImage/cdnImage';
import { pxToVw } from '@/components/productCard/common/utils';
import { ProductType } from '@/components/productCard/index.interface';
import { getExposureRecord, sendClickTrack } from '../../tempo_track';
import style from './style.module.scss';

type Props = {
  moduleData: any;
  localInfo: any;
  pageName: string;
  floorIndex: number;
  pageCode: string;
};

const adaptiveStyle = {
  bgImageWidth: pxToVw(375),
  bgImageHeight: pxToVw(320),
  productListLeft: pxToVw(108),
  productCardWidth: pxToVw(98),
  productCardHeight: pxToVw(148),
  skuImgWidth: pxToVw(98),
  skuImgHeight: pxToVw(98),
  gap: pxToVw(8),
};

export default function Module({ moduleData, localInfo, pageName, floorIndex, pageCode }: Props) {
  // 初始化页面配置对象
  const pageConfig: any = {};
  const exposureRecord = getExposureRecord(pageCode);
  // 配置初始化函数，解析模块数据
  const initConfig = (moduleData: any) => {
    // 从模块数据中提取产品池数据
    pageConfig.data = moduleData.dataBatch?.productPool[0].productPool || [];
    // 提取背景图片配置
    pageConfig.backgroundImg = moduleData.dataBatch?.productPool[0].backgroundImg || {};
  };
  initConfig(moduleData);
  if (!Array.isArray(pageConfig.data) || pageConfig.data.length === 0) {
    return null;
  }

  // 将商品列表按照两行左侧布局规则分组
  // 逻辑：奇数个商品时，最后一个商品单独成组；偶数个商品时，全部按2个一组分组
  const groupProductsForTwoRowLayout = (products: any[]): any[][] => {
    if (!Array.isArray(products) || products.length === 0) {
      return [];
    }
    const productList: any[][] = [];
    const isOdd = products.length % 2 === 1;
    const pairsCount = isOdd ? products.length - 1 : products.length;
    for (let i = 0; i < pairsCount; i += 2) {
      const pair = [
        { ...products[i], trackIndex: i },
        { ...products[i + 1], trackIndex: i + 1 },
      ];
      productList.push(pair);
    }

    if (isOdd) {
      const lastProduct = { ...products[products.length - 1], trackIndex: products.length - 1 };
      productList.push([lastProduct]);
    }

    return productList;
  };

  const productList = groupProductsForTwoRowLayout(pageConfig.data);

  const genEventTrackingInfo = (product: any, index: number) => {
    const eventTrackingInfo = {} as { [key: string]: any };
    const json_param = {
      skuid: product.skuId,
      index: product.trackIndex,
      pos: floorIndex,
      page_id: 'cms_' + pageCode,
      moduleInstanceId: 'two-row-left-horizontal-product',
      propoolid: product.productPoolId,
      algo_rd_inf: product.algoRdInfo || '',
      broker_info: product.brokerInfo ? JSON.stringify(product.brokerInfo) : '',
    };

    // 元素露出50%停留300ms触发
    const expConfig = {
      useClick: false,
      stay_duration: 0.3,
      repeated: false,
      area_rate: 0.5,
    };

    eventTrackingInfo['productClickPoint'] = () =>
      sendClickTrack.cms_TempoPage_LeftHorizontalProductPool2_Click(json_param);
    eventTrackingInfo['productExpConfig'] = {
      event_id: exposureRecord.cms_TempoPage_LeftHorizontalProductPool2_Expo,
      config: expConfig,
      json_param: json_param,
    };
    return eventTrackingInfo;
  };

  return (
    <div className={style['two-row-left-horizontal-product']}>
      <CDNImage
        // @ts-ignore
        src={pageConfig.backgroundImg.imageUrl}
        width={pageConfig.backgroundImg.imageWidth}
        height={pageConfig.backgroundImg.imageHeight}
        style={{
          width: adaptiveStyle.bgImageWidth,
          height: adaptiveStyle.bgImageHeight,
          objectFit: 'cover',
          objectPosition: 'top',
        }}
      />
      <div
        className={style.productScrollContainer}
        style={{
          top: adaptiveStyle.gap,
          left: adaptiveStyle.productListLeft,
          right: '0px',
          bottom: '0px',
          width: `calc(100% - ${adaptiveStyle.productListLeft})`,
          position: 'absolute',
        }}
      >
        <div
          className={style.productList}
          // style={{
          //   gap: adaptiveStyle.gap,
          // }}
        >
          {productList.length
            ? productList.map((product_two: any[], index: number) => {
                return (
                  <div
                    className={style.product_two}
                    key={`product_group_${index}`} // 添加唯一 key
                    style={{
                      marginRight: adaptiveStyle.gap,
                      gap: adaptiveStyle.gap,
                    }}
                  >
                    {product_two.map((item: any, subIndex: number) => {
                      const productData = {
                        type: ProductType.TYPE_1,
                        width: adaptiveStyle.productCardWidth,
                        height: adaptiveStyle.productCardHeight,
                        skuName: item.skuName,
                        skuId: item.skuId,
                        realPrice: item.realPrice,
                        originalPrice: item?.originalPriceV2,
                        originalPriceTips: item?.originalPriceDesc,
                        currency: localInfo?.currencyCode || '',
                        languageCode: localInfo?.languageCode || '',
                        trackingPointParams: genEventTrackingInfo(item, index),
                        // style
                        containerStyle: {
                          backgroundColor: '#fff',
                          marginRight: adaptiveStyle.gap,
                        },
                        skuImgKeyParams: {
                          imgUrlKey: item?.imageUrl,
                          width: 200,
                          height: 200,
                        },
                        skuImgStyle: {
                          width: adaptiveStyle.skuImgWidth,
                          height: adaptiveStyle.skuImgHeight,
                          borderRadius: '4px',
                          alignSelf: 'center',
                        },
                        priceStyle: {
                          //@ts-ignore
                          textAlign: 'center' as TextAlign,
                          marginTop: '8px',
                        },
                        realPriceJDIPriceParams: {
                          symbolStyle: { fontSize: '16px' },
                          integerStyle: { fontSize: '16px' },
                          decimalPointStyle: { fontSize: '16px' },
                          decimalPlaceStyle: { fontSize: '16px' },
                          containerStyle: { display: 'block', height: '22px', lineHeight: '22px' },
                        },
                        originalPriceJDIPriceParams: {
                          containerStyle: {
                            fontSize: '10px',
                            height: '12px',
                            lineHeight: '12px',
                            fontWeight: '500',
                            marginTop: '0px',
                          },
                        },
                        originalPriceStyle: {
                          fontSize: '10px',
                        },
                      };
                      return <ProductCard key={item.skuId} productData={productData} />; // 使用 skuId 作为 key
                    })}
                  </div>
                );
              })
            : null}
        </div>
      </div>
    </div>
  );
}
